import { useEffect, useState } from "react";
import { ArrowsClockwise, CheckCircle, Warning, Globe } from "@phosphor-icons/react";
import api from "../lib/api";
import { confirmProductionWrite } from "../lib/diagnostics";
import { toast } from "sonner";

/**
 * Iter 20.18 Phase 4 — Live Listing dry-run/manual ingest card.
 *
 * Primary launch-mode ingest path. Uses operator live/current listing
 * pages (LLF / DCG / Click / Elite) as the source of competition
 * candidates. Brave/search-index discovery becomes secondary.
 *
 * Two-step protocol mirrors the consolidated cleanup card:
 *   1. Dry-run → returns `live_listing_ingest_plan_id`.
 *   2. Apply (button enabled only after a plan_id is in hand).
 *
 * Records created here are NEVER `verified_live`. Manual review remains
 * required.
 */
export default function LiveListingIngestCard({ diag, onApplied, selectedOperator = null }) {
  const [launchOnly, setLaunchOnly] = useState(!selectedOperator);
  const [fetchDetails, setFetchDetails] = useState(true);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [busy, setBusy] = useState(null); // "dry-run" | "apply" | null
  const [plan, setPlan] = useState(null);
  const selectedName = selectedOperator?.name || "";
  const selectedKey = selectedOperator?.key || "";
  const dryRunSupported = selectedOperator?.supports?.liveListingDryRun ?? true;
  const applySupported = selectedOperator
    ? selectedOperator?.supports?.liveListingApply === true
    : true;

  const planExpiresAt = plan?.plan_expires_at
    ? new Date(plan.plan_expires_at)
    : null;

  useEffect(() => {
    setPlan(null);
    setLaunchOnly(!selectedOperator);
  }, [selectedOperator]);

  const runDryRun = async () => {
    if (!dryRunSupported) {
      toast.error("Live-listing dry-run is not enabled for this operator.");
      return;
    }
    const busyKey = selectedKey ? `${selectedKey}-dry-run` : "dry-run";
    setBusy(busyKey);
    try {
      const { data } = await api.post(
        "/admin/integrity/live-listing-ingest",
        {
          dry_run: true,
          operator: selectedName || null,
          launch_operators_only: selectedName ? false : launchOnly,
          fetch_details: fetchDetails,
          force_refresh: forceRefresh,
          limit_per_operator: 200,
          max_details: 50,
        },
      );
      setPlan(data);
      const s = data.summary || {};
      toast.success(
        `Live Listing dry-run: ${s.would_create || 0} would create, ` +
          `${s.would_update || 0} would update, ` +
          `${(s.would_skip_expired || 0) +
            (s.would_skip_listing || 0) +
            (s.would_skip_category || 0) +
            (s.would_skip_broken || 0) +
            (s.would_skip_blocked || 0)} would skip.`,
      );
    } catch (e) {
      const label = selectedName
        ? `${selectedName} dry-run`
        : "Live Listing dry-run";
      toast.error(
        `${label} failed: ` +
          (e.response?.data?.detail?.error ||
            e.response?.data?.detail ||
            e.message),
      );
    } finally {
      setBusy(null);
    }
  };

  const runApply = async (operatorIdFilter = null) => {
    if (!plan?.live_listing_ingest_plan_id) return;
    const s = plan.summary || {};
    let total = (s.would_create || 0) + (s.would_update || 0);
    let scopeLabel = "all operators";
    let scopedOp = null;
    if (operatorIdFilter) {
      scopedOp = (plan.per_operator || []).find(
        (o) => o.operator_id === operatorIdFilter,
      );
      if (scopedOp) {
        total = (scopedOp.would_create || 0) + (scopedOp.would_update || 0);
        scopeLabel = scopedOp.operator_name || operatorIdFilter;
      }
    }
    if (
      !confirmProductionWrite(
        diag,
        `Apply Live Listing Ingest plan (${scopeLabel}): ` +
          `${scopedOp ? (scopedOp.would_create || 0) : (s.would_create || 0)} new + ` +
          `${scopedOp ? (scopedOp.would_update || 0) : (s.would_update || 0)} updates ` +
          `as source_pending_review (no auto-promotion).`,
      )
    ) {
      return;
    }
    if (total === 0) {
      toast("Nothing to apply for " + scopeLabel + " — plan has 0 would_create/would_update.");
      return;
    }
    setBusy(operatorIdFilter ? `apply-${operatorIdFilter}` : "apply");
    try {
      const { data } = await api.post(
        "/admin/integrity/live-listing-ingest",
        {
          dry_run: false,
          live_listing_ingest_plan_id: plan.live_listing_ingest_plan_id,
          apply_operator_id: operatorIdFilter,
        },
      );
      toast.success(
        `Live Listing applied (${scopeLabel}): ${data.created} created, ${data.updated} updated, ` +
          `${data.skipped_already_applied} idempotent skips.`,
      );
      setPlan({ ...plan, last_apply: data });
      if (onApplied) onApplied();
    } catch (e) {
      const err =
        e.response?.data?.detail?.error ||
        e.response?.data?.detail ||
        e.message;
      toast.error("Live Listing apply failed: " + err);
    } finally {
      setBusy(null);
    }
  };

  const summary = plan?.summary || null;
  const perOperator = plan?.per_operator || [];
  const skipped = plan?.skipped_operators || [];
  const planHasUnsupportedApply = perOperator.some(() => !applySupported);
  const isDryRunOnlyPlan = planHasUnsupportedApply && perOperator.length === 1;

  return (
    <div
      className="rounded-lg border border-[var(--cc-gold)]/30 bg-gradient-to-br from-[var(--cc-gold)]/[0.04] to-transparent p-5 space-y-4"
      data-testid="live-listing-ingest-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Globe size={18} weight="bold" className="text-[var(--cc-gold)]" />
            <h3
              className="text-lg font-semibold text-white"
              data-testid="live-listing-ingest-title"
            >
              Live Listing Dry-run Plan Preview
            </h3>
          </div>
          <p className="text-xs text-white/60 max-w-2xl leading-relaxed">
            Dry-run plan preview for operator live/current listing pages. If
            this legacy/manual plan is applied, records land as{" "}
            <code className="px-1 py-0.5 bg-white/5 mx-0.5 rounded text-[10px]">
              source_pending_review
            </code>{" "}
            and require manual approval. Never auto-promoted to{" "}
            <strong>verified_live</strong>.
          </p>
        </div>
      </div>

      {/* Options */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
        {!selectedName && (
          <label className="inline-flex items-center gap-2 text-white/80">
            <input
              type="checkbox"
              checked={launchOnly}
              onChange={(e) => setLaunchOnly(e.target.checked)}
              data-testid="live-listing-launch-only-toggle"
            />
            Launch operators only
          </label>
        )}
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={fetchDetails}
            onChange={(e) => setFetchDetails(e.target.checked)}
            data-testid="live-listing-fetch-details-toggle"
          />
          Fetch detail pages (page-role judge)
        </label>
        <label className="inline-flex items-center gap-2 text-white/80">
          <input
            type="checkbox"
            checked={forceRefresh}
            onChange={(e) => setForceRefresh(e.target.checked)}
            data-testid="live-listing-force-refresh-toggle"
          />
          Force refresh
        </label>
        {selectedName && (
          <div className="text-white/60">
            Operator: <span className="text-white/85">{selectedName}</span>
          </div>
        )}
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => runDryRun()}
          disabled={busy != null || !dryRunSupported}
          data-testid={selectedKey === "elite" ? "live-listing-elite-dry-run-btn" : "live-listing-dry-run-btn"}
          className="btn-gold px-4 py-2 rounded text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <ArrowsClockwise size={14} weight="bold" />
          {busy === `${selectedKey}-dry-run` || busy === "dry-run"
            ? "Running dry-run…"
            : selectedName
              ? `Build ${selectedName} dry-run preview`
              : "Dry-run plan preview"}
        </button>
        {!planHasUnsupportedApply && applySupported && (
          <button
            onClick={() => runApply(null)}
            disabled={busy != null || !plan?.live_listing_ingest_plan_id}
            data-testid="live-listing-apply-btn"
            className="px-4 py-2 rounded text-xs inline-flex items-center gap-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <CheckCircle size={14} weight="bold" />
            {busy === "apply" ? "Applying..." : "Apply ingest plan to review queue"}
          </button>
        )}
        {plan?.live_listing_ingest_plan_id && (
          <span
            className="text-[10px] text-white/50 font-mono"
            data-testid="live-listing-plan-id"
          >
            plan {plan.live_listing_ingest_plan_id.slice(0, 8)}{" "}
            {planExpiresAt
              ? `· expires ${planExpiresAt.toLocaleTimeString("en-GB")}`
              : ""}
          </span>
        )}
      </div>

      {isDryRunOnlyPlan && (
        <div
          className="rounded-md border border-sky-400/30 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="live-listing-dry-run-only"
        >
          Dry-run plan preview loaded. This card will not apply records for
          this selected operator; use the Operator Automation card for
          settings-controlled apply actions.
        </div>
      )}

      {/* Excluded operators chips */}
      {skipped.length > 0 && (
        <div
          className="flex items-center gap-2 flex-wrap text-[11px]"
          data-testid="live-listing-skipped-operators"
        >
          <span className="text-white/40">Excluded:</span>
          {skipped.map((s) => (
            <span
              key={s.name}
              title={`${s.skip_reason}: ${s.detail || ""}`}
              data-testid={`live-listing-skipped-${(s.name || "").replace(/\s+/g, "-").toLowerCase()}`}
              className="px-2 py-0.5 rounded-full bg-white/5 text-white/50 border border-white/10 inline-flex items-center gap-1"
            >
              <Warning size={10} /> {s.name}
            </span>
          ))}
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div
          className="rounded-md bg-black/30 border border-white/10 p-3"
          data-testid="live-listing-summary"
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 text-[11px]">
            <Stat
              label="Cards discovered"
              value={summary.cards_found}
              testId="live-listing-cards-found"
            />
            <Stat
              label="Accepted"
              value={summary.cards_accepted}
              testId="live-listing-cards-accepted"
              tone="ok"
            />
            <Stat
              label="Rejected"
              value={summary.cards_rejected}
              testId="live-listing-cards-rejected"
              tone="muted"
            />
            <Stat
              label="Details"
              value={summary.details_fetched}
              testId="live-listing-details-fetched"
            />
            <Stat
              label="Would create"
              value={summary.would_create}
              testId="live-listing-would-create"
              tone="ok"
            />
            <Stat
              label="Would update"
              value={summary.would_update}
              testId="live-listing-would-update"
              tone="ok"
            />
            <Stat
              label="Skip · expired"
              value={summary.would_skip_expired}
              testId="live-listing-skip-expired"
              tone="warn"
            />
            <Stat
              label="Skip · listing"
              value={summary.would_skip_listing}
              testId="live-listing-skip-listing"
              tone="warn"
            />
            <Stat
              label="Skip · category"
              value={summary.would_skip_category}
              testId="live-listing-skip-category"
              tone="warn"
            />
            <Stat
              label="Skip · broken"
              value={summary.would_skip_broken}
              testId="live-listing-skip-broken"
              tone="warn"
            />
            <Stat
              label="Skip · blocked"
              value={summary.would_skip_blocked}
              testId="live-listing-skip-blocked"
              tone="warn"
            />
            <Stat
              label="Errors"
              value={summary.errors}
              testId="live-listing-errors"
              tone={summary.errors > 0 ? "bad" : "muted"}
            />
          </div>
        </div>
      )}

      {/* Per-operator breakdown */}
      {perOperator.length > 0 && (
        <div className="space-y-2" data-testid="live-listing-per-operator">
          {perOperator.map((op) => (
            <details
              key={op.operator_id}
              className="rounded border border-white/10 bg-white/[0.02] px-3 py-2"
              data-testid={`live-listing-op-${op.operator_key}`}
            >
              <summary className="cursor-pointer flex items-center justify-between gap-2 text-xs">
                <span className="font-semibold text-white">
                  {op.operator_name}
                </span>
                <span className="flex items-center gap-3">
                  <span className="text-white/60 font-mono-num">
                    found {op.cards_found} · accepted {op.cards_accepted} ·
                    create {op.would_create} · update {op.would_update} ·
                    skip {op.would_skip_expired + op.would_skip_listing + op.would_skip_category + op.would_skip_broken + op.would_skip_blocked}
                  </span>
                  {plan?.live_listing_ingest_plan_id &&
                    applySupported &&
                    (op.would_create + op.would_update) > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          runApply(op.operator_id);
                        }}
                        disabled={busy != null}
                        data-testid={`live-listing-apply-operator-${op.operator_key}`}
                        className="px-2 py-1 rounded text-[10px] inline-flex items-center gap-1 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 border border-emerald-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <CheckCircle size={10} weight="bold" />
                        {busy === `apply-${op.operator_id}` ? "Applying..." : `Apply ingest plan for ${op.operator_name}`}
                      </button>
                  )}
                  {!applySupported && (
                    <span
                      className="px-2 py-1 rounded text-[10px] bg-sky-400/10 text-sky-100 border border-sky-400/30"
                      data-testid="live-listing-elite-apply-disabled"
                    >
                      dry-run only
                    </span>
                  )}
                </span>
              </summary>
              <div className="mt-2 space-y-2 text-[11px]">
                {(op.listing_urls || []).map((u, i) => (
                  <div
                    key={i}
                    className="text-white/60 flex flex-wrap gap-x-3 gap-y-1"
                  >
                    <span>URL:</span>
                    <code className="text-white/80 break-all">{u.listing_url}</code>
                    <span>HTTP {u.listing_fetch_status}</span>
                    <span>html {Math.round((u.html_length || 0) / 1024)}KB</span>
                    {u.parser_used && (
                      <span className="text-white/40">parser: {u.parser_used}</span>
                    )}
                    {(u.errors || []).length > 0 && (
                      <span className="text-red-300">
                        errors: {u.errors.join(", ")}
                      </span>
                    )}
                  </div>
                ))}
                {(op.sample_accepted || []).length > 0 && (
                  <SampleList
                    title="Sample accepted"
                    samples={op.sample_accepted}
                    marker="+"
                    tone="text-sky-300/80"
                  />
                )}
                {(op.sample_would_create || []).length > 0 && (
                  <SampleList
                    title="Sample would-create"
                    samples={op.sample_would_create}
                    marker="+"
                    tone="text-emerald-300/80"
                  />
                )}
                {(op.sample_would_update || []).length > 0 && (
                  <SampleList
                    title="Sample would-update"
                    samples={op.sample_would_update}
                    marker="~"
                    tone="text-amber-300/80"
                  />
                )}
                {(op.sample_rejected || []).length > 0 && (
                  <div>
                    <div className="text-white/40 mb-1">Sample rejected:</div>
                    <ul className="space-y-0.5">
                      {op.sample_rejected.map((s, i) => (
                        <li key={i} className="text-white/50">
                          <code className="text-red-300/70">−</code>{" "}
                          <span className="text-white/60">{s.reason}</span>{" "}
                          <code className="text-white/40 break-all">
                            {s.href}
                          </code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </details>
          ))}
        </div>
      )}

      {plan?.last_apply && (
        <div
          className="rounded-md bg-emerald-500/10 border border-emerald-500/30 p-3 text-xs text-emerald-200"
          data-testid="live-listing-last-apply"
        >
          Applied at {new Date(plan.last_apply.applied_at).toLocaleString("en-GB")}:
          created {plan.last_apply.created}, updated {plan.last_apply.updated},
          idempotent skips {plan.last_apply.skipped_already_applied}.
        </div>
      )}
    </div>
  );
}

function SampleList({ title, samples, marker, tone }) {
  return (
    <div>
      <div className="text-white/40 mb-1">{title}:</div>
      <ul className="space-y-1">
        {samples.map((s, i) => {
          const date = s.draw_date || s.closing_date || null;
          const imagePresent = Boolean(s.image_url || s.source_image_url);
          return (
            <li key={i} className="text-white/70">
              <div>
                <code className={tone}>{marker}</code>{" "}
                <span>{s.title || s.prize_name || "Untitled"}</span>
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-white/40">
                <span>price {formatEvidenceValue(s.ticket_price, "£")}</span>
                {s.ticket_price_source && <span>price source {s.ticket_price_source}</span>}
                <span>cap {formatEvidenceValue(s.max_tickets)}</span>
                {s.max_tickets_source && <span>cap source {s.max_tickets_source}</span>}
                <span>date {date || "missing"}</span>
                {s.date_source && <span>date source {s.date_source}</span>}
                <span>image {imagePresent ? "present" : "missing"}</span>
                <span>category {s.category || "missing"}</span>
                <span>status {s.sold_out ? "sold out" : "active or review"}</span>
                {s.gate_role && <span>role {s.gate_role}</span>}
              </div>
              {(s.source_url || s.detail_url || s.href) && (
                <code className="block mt-0.5 text-white/35 break-all">
                  {s.source_url || s.detail_url || s.href}
                </code>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function formatEvidenceValue(value, prefix = "") {
  if (value === null || value === undefined || value === "") return "missing";
  return `${prefix}${value}`;
}

function Stat({ label, value, testId, tone }) {
  const toneCls =
    tone === "ok"
      ? "bg-emerald-500/15 text-emerald-200"
      : tone === "warn"
        ? "bg-amber-500/15 text-amber-200"
        : tone === "bad"
          ? "bg-red-500/15 text-red-200"
          : tone === "muted"
            ? "bg-white/5 text-white/60"
            : "bg-white/5 text-white/80";
  return (
    <div
      className={`rounded px-2 py-1.5 ${toneCls}`}
      data-testid={testId}
    >
      <div className="text-[9px] uppercase tracking-wide opacity-70">
        {label}
      </div>
      <div className="text-sm font-semibold font-mono-num">{value ?? 0}</div>
    </div>
  );
}
